import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { UserSidebar } from '@/components/Layout/UserSidebar';
import { BoxDetailsSidebar } from '@/components/BoxDetailsSidebar';
import { projectService } from '@/services/projectService';
import { boxService } from '@/services/boxService';
import { boxTypeService } from '@/services/boxTypeService';
import { Box } from '@/types';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/authStore';
import { useTheme } from '@/hooks/useTheme';
import { ProjectLogo } from '@/components/ProjectLogo';

export function UserDevicesPage() {
  const { logout } = useAuth();
  const { isRoot, isAdmin } = useAuthStore();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const projectIdInt = projectId ? Number(projectId) : null;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [boxDetailsSidebarOpen, setBoxDetailsSidebarOpen] = useState(false);
  const [selectedBoxId, setSelectedBoxId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'online' | 'offline'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { data: projectsResponse, isLoading: isLoadingProjects } = useQuery({
    queryKey: ['user-projects'],
    queryFn: () => projectService.getProjects({ page: 1, itemsPerPage: 100 }),
  });

  const userProjects = useMemo(() => {
    return projectsResponse?.data || [];
  }, [projectsResponse]);

  const currentProject = useMemo(() => {
    if (!projectIdInt) return null;
    return userProjects.find((p) => p.id === projectIdInt) ?? null;
  }, [userProjects, projectIdInt]);

  const shouldRedirectAdmin = !currentProject && projectIdInt && (isRoot() || isAdmin());
  useEffect(() => {
    if (shouldRedirectAdmin) {
      navigate(isRoot() ? '/admin/dashboard' : '/admin/users', { replace: true });
    }
  }, [shouldRedirectAdmin, isRoot, isAdmin, navigate]);

  if (!projectIdInt) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <span className="material-icons-outlined text-red-500 text-6xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Projet requis</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-4">Veuillez sélectionner un projet pour accéder aux équipements.</p>
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Choisir un projet
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Fetch box types
  const { data: boxTypes = [] } = useQuery({
    queryKey: ['box-types'],
    queryFn: () => boxTypeService.getBoxTypes(),
  });

  const { data: boxesResponse, isLoading: isLoadingBoxes } = useQuery({
    queryKey: ['project-boxes', currentProject?.id, searchQuery, statusFilter, typeFilter, currentPage],
    queryFn: async () => {
      if (!currentProject) return { data: [], totalItems: 0, currentPage: 1, itemsPerPage: 10, totalPages: 0, hasNextPage: false, hasPreviousPage: false };

      const status = statusFilter !== 'all' ? statusFilter : undefined;
      return boxService.getBoxesByProject(
        currentProject.id,
        searchQuery || undefined,
        currentPage,
        itemsPerPage,
        status,
        typeFilter !== 'all' ? typeFilter : undefined
      );
    },
    enabled: !!currentProject,
  });

  const boxes = useMemo(() => {
    const boxesList = boxesResponse?.data || [];
    if (!boxesList || boxesList.length === 0) return [];

    return [...boxesList].sort((a: Box, b: Box) => {
      if (a.isOnline === true && b.isOnline === false) return -1;
      if (a.isOnline === false && b.isOnline === true) return 1;
      return 0;
    });
  }, [boxesResponse]);
  const totalItems = boxesResponse?.totalItems || 0;
  const totalPages = boxesResponse?.totalPages || 0;

  const { data: statsData, isLoading: isLoadingStats } = useQuery({
    queryKey: ['project-boxes-stats', currentProject?.id, 'equipment'],
    queryFn: async () => {
      if (!currentProject) return null;
      return projectService.getBoxesStats(currentProject.id, 'equipment');
    },
    enabled: !!currentProject,
  });

  const stats = useMemo(() => {
    if (!statsData) {
      return { total: 0, online: 0, offline: 0, warnings: 0, onlinePercentage: 0 };
    }

    return {
      total: statsData.total,
      online: statsData.online,
      offline: statsData.offline,
      warnings: statsData.offline,
      onlinePercentage: statsData.onlinePercentage,
    };
  }, [statsData]);

  const getBoxTypeIcon = (typeName?: string) => {
    const type = typeName?.toLowerCase() || '';
    if (type.includes('zipato') || type.includes('gateway')) return 'router';
    if (type.includes('thermostat') || type.includes('temp')) return 'thermostat';
    if (type.includes('light') || type.includes('eclairage')) return 'lightbulb';
    if (type.includes('security') || type.includes('securite')) return 'security';
    if (type.includes('elevator') || type.includes('ascenseur')) return 'elevator';
    if (type.includes('cvc') || type.includes('climat')) return 'ac_unit';
    return 'devices';
  };

  const getBoxTypeColorClasses = (typeName?: string) => {
    const type = typeName?.toLowerCase() || '';
    if (type.includes('zipato') || type.includes('gateway')) return { bg: 'bg-indigo-500/10', text: 'text-indigo-400', border: 'border-indigo-500/20' };
    if (type.includes('thermostat') || type.includes('temp')) return { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' };
    if (type.includes('light') || type.includes('eclairage')) return { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20' };
    if (type.includes('security') || type.includes('securite')) return { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' };
    if (type.includes('elevator') || type.includes('ascenseur')) return { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' };
    if (type.includes('cvc') || type.includes('climat')) return { bg: 'bg-teal-500/10', text: 'text-teal-400', border: 'border-teal-500/20' };
    return { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/20' };
  };

  const formatLastUpdate = (dateString?: string) => {
    if (!dateString) return { date: '--', time: '--:--' };
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return { date: "Aujourd'hui", time: date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) };
    } else if (diffDays === 1) {
      return { date: 'Hier', time: date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) };
    } else {
      return { date: `${diffDays} jours`, time: '--:--' };
    }
  };

  if (isLoadingProjects) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-icons-outlined animate-spin text-primary text-6xl mb-4">progress_activity</span>
            <p className="text-slate-500 dark:text-slate-400">Chargement des projets...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentProject) {
    if (isRoot() || isAdmin()) {
      return (
        <div className="flex h-screen bg-slate-50 dark:bg-slate-900 items-center justify-center">
          <span className="material-icons-outlined animate-spin text-primary text-4xl">progress_activity</span>
        </div>
      );
    }
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-icons-outlined text-slate-400 text-6xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Aucun projet assigné</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-6">Vous n'avez pas encore accès à un projet. Contactez votre administrateur pour obtenir un accès.</p>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 rounded-lg transition-colors"
            >
              <span className="material-icons-outlined">logout</span>
              Se déconnecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 text-slate-900 dark:bg-slate-900 dark:text-slate-100 h-screen flex overflow-hidden font-display">
      <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative bg-slate-50 dark:bg-slate-900">
        {/* Header */}
        <header className="h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-6 sm:px-8 z-20 shadow-sm sticky top-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <span className="material-icons-outlined" style={{ fontSize: '20px' }}>menu</span>
            </button>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
              Gestion des Appareils
            </h1>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-2"></div>
            <div className="flex items-center text-sm text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800/50 px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700/50">
              <span className="material-icons-outlined mr-2 text-slate-500" style={{ fontSize: '16px' }}>domain</span>
              {currentProject.title} • {currentProject.city || 'Paris'}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="hidden md:flex items-center px-3 py-1 bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-full text-xs font-medium text-slate-600 dark:text-slate-300">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse shadow-sm shadow-green-500/50"></span>
              Système en ligne
            </div>
            <div className="h-8 w-px bg-slate-200 dark:bg-slate-700 mx-1"></div>
            <ProjectLogo project={currentProject} size="md" className="rounded-lg" />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? <span className="material-icons-outlined">light_mode</span> : <span className="material-icons-outlined">dark_mode</span>}
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-0 scroll-smooth bg-slate-50 dark:bg-slate-900">
          <main className="max-w-7xl mx-auto px-6 sm:px-8 py-8 w-full">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 flex items-center shadow-sm">
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 mr-4">
                  <span className="material-icons-outlined" style={{ fontSize: '24px' }}>router</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Total Appareils</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{stats.total}</p>
                </div>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 flex items-center shadow-sm">
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 mr-4">
                  <span className="material-icons-outlined" style={{ fontSize: '24px' }}>check_circle</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">En Ligne</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{stats.online}</p>
                </div>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 flex items-center shadow-sm">
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 mr-4">
                  <span className="material-icons-outlined" style={{ fontSize: '24px' }}>error_outline</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Hors Ligne</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{stats.offline}</p>
                </div>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 flex items-center shadow-sm">
                <div className="p-3 rounded-lg bg-teal-500/10 border border-teal-500/20 text-teal-400 mr-4">
                  <span className="material-icons-outlined" style={{ fontSize: '24px' }}>percent</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Boxes en ligne</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{stats.onlinePercentage.toFixed(1)}%</p>
                </div>
              </div>
            </div>

            {/* Filters and Actions */}
            <div className="bg-white dark:bg-slate-800 rounded-t-xl border border-slate-200 dark:border-slate-700 border-b-0 p-4 flex flex-col sm:flex-row justify-between items-center gap-4 shadow-sm">
              <div className="flex items-center gap-3 w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0">
                <div className="relative">
                  <select
                    className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-400 dark:hover:border-slate-600 transition-all shadow-sm appearance-none pr-8"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value as 'all' | 'online' | 'offline');
                      setCurrentPage(1);
                    }}
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="online">En ligne</option>
                    <option value="offline">Hors ligne</option>
                  </select>
                  <span className="material-icons-outlined absolute right-2 top-2.5 text-slate-500 pointer-events-none" style={{ fontSize: '20px' }}>expand_more</span>
                </div>
                <div className="relative">
                  <select
                    className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-400 dark:hover:border-slate-600 transition-all shadow-sm appearance-none pr-8"
                    value={typeFilter}
                    onChange={(e) => {
                      setTypeFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="all">Type d'appareil</option>
                    {boxTypes.map((type: any) => (
                      <option key={type.id} value={type.id}>
                        {type.type}
                      </option>
                    ))}
                  </select>
                  <span className="material-icons-outlined absolute right-2 top-2.5 text-slate-500 pointer-events-none" style={{ fontSize: '20px' }}>expand_more</span>
                </div>
                <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-2 hidden sm:block"></div>
                <div className="relative">
                  <input
                    className="w-64 pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all shadow-sm"
                    placeholder="Rechercher par nom ou code..."
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                  <span className="material-icons-outlined absolute left-3 top-2.5 text-slate-500" style={{ fontSize: '20px' }}>search</span>
                </div>
                <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-2 hidden sm:block"></div>
                {(statusFilter !== 'all' || typeFilter !== 'all' || searchQuery) && (
                  <button
                    className="text-slate-500 hover:text-primary text-sm font-medium transition-colors"
                    onClick={() => {
                      setStatusFilter('all');
                      setTypeFilter('all');
                      setSearchQuery('');
                      setCurrentPage(1);
                    }}
                  >
                    Effacer les filtres
                  </button>
                )}
              </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-b-xl shadow-sm overflow-hidden mb-8">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                      <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer group hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors">
                        <div className="flex items-center gap-1">
                          Nom
                          <span className="material-icons-outlined opacity-0 group-hover:opacity-100 text-slate-400" style={{ fontSize: '14px' }}>arrow_upward</span>
                        </div>
                      </th>
                      <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Localisation</th>
                      <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</th>
                      <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Dernière Synchro</th>
                      <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {isLoadingBoxes ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center">
                          <span className="material-icons-outlined animate-spin text-primary text-4xl">progress_activity</span>
                          <p className="mt-4 text-slate-500 dark:text-slate-400">Chargement des appareils...</p>
                        </td>
                      </tr>
                    ) : boxes.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center">
                          <span className="material-icons-outlined text-slate-400 dark:text-slate-500 text-6xl mb-4">devices</span>
                          <p className="text-slate-500 dark:text-slate-400">Aucun appareil trouvé</p>
                        </td>
                      </tr>
                    ) : (
                      boxes.map((box: Box) => {
                        const boxType = typeof box.type === 'object' ? box.type : null;
                        const typeName = boxType?.type || 'Inconnu';
                        const icon = getBoxTypeIcon(typeName);
                        const colorClasses = getBoxTypeColorClasses(typeName);
                        const lastUpdate = formatLastUpdate(box.lastUpdate);

                        return (
                          <tr key={box.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors group">
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <div className={`h-10 w-10 flex-shrink-0 rounded-lg ${colorClasses.bg} ${colorClasses.text} flex items-center justify-center mr-3 border ${colorClasses.border}`}>
                                  <span className="material-icons-outlined" style={{ fontSize: '20px' }}>{icon}</span>
                                </div>
                                <div>
                                  <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">{box.name}</div>
                                  <div className="text-xs text-slate-400 dark:text-slate-500">ID: {box.code || box.uuid || `BOX-${box.id}`}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-slate-500 dark:text-slate-400">{typeName}</span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-slate-500 dark:text-slate-400">{currentProject.title}</span>
                            </td>
                            <td className="px-6 py-4">
                              {box.isOnline ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20">
                                  <span className="h-1.5 w-1.5 rounded-full bg-green-500 mr-1.5 shadow-[0_0_8px_rgba(34,197,94,0.6)]"></span>
                                  En Ligne
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20">
                                  <span className="h-1.5 w-1.5 rounded-full bg-red-500 mr-1.5 shadow-[0_0_8px_rgba(239,68,68,0.6)]"></span>
                                  Hors Ligne
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="text-sm text-slate-500 dark:text-slate-400">{lastUpdate.date}</div>
                              <div className="text-xs text-slate-400 dark:text-slate-500">{lastUpdate.time}</div>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedBoxId(box.id);
                                  setBoxDetailsSidebarOpen(true);
                                }}
                                className="text-slate-400 dark:text-slate-500 hover:text-primary transition-colors p-1"
                                title="Voir les détails"
                              >
                                <span className="material-icons-outlined" style={{ fontSize: '20px' }}>visibility</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white dark:bg-slate-800 px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    Affichage de <span className="font-medium text-slate-800 dark:text-slate-200">{(currentPage - 1) * itemsPerPage + 1}</span> à{' '}
                    <span className="font-medium text-slate-800 dark:text-slate-200">{Math.min(currentPage * itemsPerPage, totalItems)}</span> sur{' '}
                    <span className="font-medium text-slate-800 dark:text-slate-200">{totalItems}</span> résultats
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      className="px-3 py-1 border border-slate-300 dark:border-slate-700 rounded-md text-sm text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    >
                      Précédent
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`px-3 py-1 border rounded-md text-sm ${
                            currentPage === pageNum
                              ? 'bg-primary text-white border-primary shadow-md shadow-primary/20'
                              : 'border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                          }`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    {totalPages > 5 && currentPage < totalPages - 2 && (
                      <span className="text-slate-400 dark:text-slate-600">...</span>
                    )}
                    {totalPages > 5 && (
                      <button
                        className="px-3 py-1 border border-slate-300 dark:border-slate-700 rounded-md text-sm text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
                        onClick={() => setCurrentPage(totalPages)}
                      >
                        {totalPages}
                      </button>
                    )}
                    <button
                      className="px-3 py-1 border border-slate-300 dark:border-slate-700 rounded-md text-sm text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      <BoxDetailsSidebar
        boxId={selectedBoxId}
        isOpen={boxDetailsSidebarOpen}
        onClose={() => {
          setBoxDetailsSidebarOpen(false);
          setSelectedBoxId(null);
        }}
        currentProjectId={currentProject?.id || 0}
      />
    </div>
  );
}
